import React, { useState } from 'react';
import Modal from '../common/Modal';
import { TeamService } from '../../services/team.service';
import toast from 'react-hot-toast';
import Button from '../common/Button';

interface DeleteTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  teamName: string;
  onDeleted: () => void;
  isDepartment?: boolean;
  role_id: number;
}

type DeleteOption = 'remove-members' | 'delete-members';

const DeleteTeamModal: React.FC<DeleteTeamModalProps> = ({
  isOpen,
  onClose,
  teamId,
  teamName,
  onDeleted,
  isDepartment = false,
  role_id
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [deleteOption, setDeleteOption] = useState<DeleteOption>(role_id === 1 ? 'delete-members' : 'remove-members');
  const [error, setError] = useState('');

  const handleDelete = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Pass true if we want to delete members, false if we just want to remove them
      const deleteUsers = deleteOption === 'delete-members';
      await TeamService.deleteTeam(teamId, deleteUsers);

      toast.success('Team deleted successfully');
      onDeleted();
      onClose();
    } catch (error) {
      console.error('Error deleting team:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete team');
      toast.error('Failed to delete team');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Team"
    >
      <div className="p-4 space-y-4">
        <p className="text-gray-600">
          Are you sure you want to delete <span className="font-medium">{teamName}</span>?
          {!isDepartment && (
            <span> This will also delete all teams within this organization.</span>
          )}
        </p>

        <div className="bg-yellow-50 p-3 rounded-md text-yellow-800 text-sm">
          <p className="font-semibold">Warning:</p>
          <p>This action cannot be undone.</p>
        </div>

        {role_id !== 1 && (
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">Choose deletion option:</p>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  type="radio"
                  id="remove-members"
                  name="delete-option"
                  value="remove-members"
                  checked={deleteOption === 'remove-members'}
                  onChange={(e) => setDeleteOption(e.target.value as DeleteOption)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="remove-members" className="ml-2 text-sm text-gray-700">
                  Delete team with all associated data but keep members
                </label>
              </div>
              <div className="flex items-center">
                <input
                  type="radio"
                  id="delete-members"
                  name="delete-option"
                  value="delete-members"
                  checked={deleteOption === 'delete-members'}
                  onChange={(e) => setDeleteOption(e.target.value as DeleteOption)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="delete-members" className="ml-2 text-sm text-gray-700">
                  Delete team data and remove members permanently
                </label>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="text-red-500 text-sm p-2 bg-red-50 rounded-md">
            {error}
          </div>
        )}

        <div className="flex justify-end space-x-3 mt-4">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            loading={isLoading}
          >
            Delete
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteTeamModal; 